import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Bar, defaults } from 'react-chartjs-2';

import Loader from '#/components/Loader';
import NotFound from '#/components/NotFound';
import DashboardContext from '#/context';
import useWidget from '#/hooks/useWidget';
import { ObjectUtils } from '#/utils/utils';

defaults.global.elements.rectangle.borderWidth = {
  top: 1,
  right: 0,
  bottom: 0,
  left: 0,
};
defaults.global.elements.rectangle.borderColor = '#fff';

function BarChart({ dashboardWidgetId, endpoint, ordination, exhibition, widgetBindDtos, auxiliaryOptionsDto }) {
  const { filters, localFilters, date, polling } = useContext(DashboardContext);
  const { serviceData } = useWidget(
    endpoint,
    filters,
    localFilters,
    date,
    polling,
    dashboardWidgetId,
    null,
    null,
    widgetBindDtos,
    auxiliaryOptionsDto,
    ordination,
    exhibition
  );

  function hasValue() {
    if (!serviceData.datasets) return 0;
    
    const validValues = serviceData.datasets.filter(({ data }) => {
      if (!data) return 0;

      let allValuesSummed;
      if(data !== null && data !== undefined && data.length > 0) {
        allValuesSummed = data.reduce(
          (accumulator, currentValue) => accumulator + currentValue
        );
      } else {
        allValuesSummed = 0;
      }
      
      return allValuesSummed !== 0;
    });
    return validValues.length;
  }

  function renderBarChart() {
    return hasValue() ? (
      <Bar
        data={serviceData}
        options={{
          maintainAspectRatio: false,
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              padding: 20,
              fontSize: 13,
              boxWidth: 8,
              borderWidth: 0,
            },
          },
          layout: {
            padding: {
              top: 22,
              left: 16,
              right: 16,
            },
          },
          scales: {
            xAxes: [
              {
                stacked: true,
                gridLines: {
                  display: false,
                },
                ticks: {
                  callback: function(value) {
                    let textLength = 55;
                    return ObjectUtils.abbreviatonEllipsis(value, textLength);
                  },
                },
              },
            ],
            yAxes: [
              {
                stacked: true,
              },
            ],
          },
          tooltips: {
            callbacks: {
                label: function(tooltipItem, data) {
                    const label = (data.datasets[tooltipItem.datasetIndex] && data.datasets[tooltipItem.datasetIndex].label) || '';
                    const value = (data.datasets[tooltipItem.datasetIndex] && data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]) || 0;
                    const tooltipValue = (data.datasets[tooltipItem.datasetIndex] && data.datasets[tooltipItem.datasetIndex].tooltipValue) || value;

                    let total;
                    if (serviceData.tooltipPercentageTotalCountAllDatasets) {
                      total = data.datasets.map(dataset => dataset.data.reduce((a, b) => a + b)).reduce((a, b) => (a + b));
                    } else {
                      total = data.datasets.map(dataset => (dataset.data && dataset.data[tooltipItem.index]) || 0).reduce((a, b) => a + b);
                    }

                    const percent = (value / total * 100).toFixed(1);
                    
                    return ` ${label} ${percent}% (${tooltipValue})`;
                }
            }
          }
        }}
      />
    ) : (
      <NotFound />
    );
  }

  return serviceData ? renderBarChart() : <Loader />;
}

BarChart.propTypes = {
  endpoint: PropTypes.shape({
    endpointType: PropTypes.string,
    endpointUrl: PropTypes.string,
  }),
};

export default BarChart;
